import Script from "next/script";
import React, { useCallback, useMemo, useRef, useState } from "react";
import settings from "../settings";
const { GAME_UID, APP_ENV } = settings;

/**
 * Logs debug messages to the console. Comment/Un-comment f(x) body to toggle all analytics debugging.
 */
function logMsg(_msg: string): void {
  if (APP_ENV !== "production") {
    // console.debug(_msg);
  }
}

export const AnalyticsScripts = React.memo(() => (
  <>
    <Script
      id={`nascar_${GAME_UID}-analytics-script-1`}
      key={`nascar_${GAME_UID}-analytics-script-1`}
      src="https://www.nascar.com/wp-content/themes/ndms/js/analytics/site-catalyst-adobe-visitor-api.js"
      strategy="afterInteractive"
      async={true}
    />
    <Script
      id={`nascar_${GAME_UID}-analytics-script-2`}
      key={`nascar_${GAME_UID}-analytics-script-2`}
      src="https://www.nascar.com/wp-content/themes/ndms/js/analytics/site-catalyst-s-code.js"
      strategy="afterInteractive"
      async={true}
    />
    <Script
      id={`nascar_${GAME_UID}-analytics-script-3`}
      key={`nascar_${GAME_UID}-analytics-script-3`}
      strategy="lazyOnload"
    >{`
        var _comscore = _comscore || [];
        _comscore.push({ c2:17728541 });
        (function() {
            var s = document.createElement("script"), el = document.getElementsByTagName("script")[0];
            s.async = true;
            s.src = (document.location.protocol == "https:" ? "https://sb" : "http://b") + ".scorecardresearch.com/beacon.js";
            el.parentNode.insertBefore(s, el);
        })();
      `}</Script>
    <noscript>
      <img src="http://b.scorecardresearch.com/p?c2=17728541&cj=1" alt="" />
    </noscript>
    <Script
      id={`nascar_${GAME_UID}-analytics-script-4`}
      key={`nascar_${GAME_UID}-analytics-script-4`}
      strategy="lazyOnload"
    >{`
        var _sf_async_config = _sf_async_config || {};
        /** CONFIGURATION START **/
        _sf_async_config.uid = 50652;
        _sf_async_config.domain = 'nascar.com';
        _sf_async_config.flickerControl = false;
        _sf_async_config.useCanonical = true;
        _sf_async_config.useCanonicalDomain = true;
        /** CONFIGURATION END **/
        var _sf_startpt = (new Date()).getTime();
      `}</Script>
    <Script
      id={`nascar_${GAME_UID}-analytics-script-5`}
      key={`nascar_${GAME_UID}-analytics-script-5`}
      src="https://www.nascar.com/wp-content/themes/ndms/js/analytics/chartbeat.min.js"
      strategy="lazyOnload"
    />
  </>
));

/**
 * Hook to trigger analytics events & utilize the analytics engine.
 */
export default function useAnalytics() {
  const pageBase = "nscr-en_US:playoffsgrid";
  const eventBase = "playoffsgrid";

  const queue: QueueItem[] = useMemo(() => [], []);
  const backoffMs = useRef(200);
  const timeoutId = useRef<NodeJS.Timeout | number | null>(null);
  const [currentPage, setCurrentPage] = useState("");

  const processQueue = useCallback(() => {
    if (typeof window !== "undefined" && window.s && window.s.t && window.s.tl) {
      timeoutId.current = null;
      const s = window.s;
      while (queue.length) {
        const payload = queue.shift();
        logMsg(`🚀 Analytics: Event Fired:\n${JSON.stringify(payload, null, 2)}`);
        if (payload.eventName) {
          s.linkTrackVars = "events,eVar53,prop22";
          s.linkTrackEvents = "event23";
          s.eVar53 = payload.eventName;
          s.prop22 = s.pageName;
          s.events = "event23";
          window.s.tl(window, "o", payload.eventName);
        } else {
          s.channel = "fantasy";
          s.pageName = payload.pageName;
          s.t();
        }
      }
    } else {
      if (backoffMs.current < 7000) {
        // after timeout ms expand to 8000 giveup retrying to look for required config elements.
        logMsg(`📡 Analytics: debouncing ${backoffMs}ms | Checking for Analytics config…`);
        timeoutId.current = setTimeout(processQueue, backoffMs.current);
        backoffMs.current = Math.floor(backoffMs.current * 1.25);
      } else {
        const msg = `📡 Analytics Error: Unable to process analytics queue. Analytics config not found.`;
        // logMsg(msg);
        console.error(msg);
      }
    }
  }, [queue]);
  const enqueue = useCallback(
    (payload: QueueItem) => {
      queue.push(payload);
      if (!timeoutId.current) {
        if (window.requestIdleCallback) {
          timeoutId.current = window.requestIdleCallback(processQueue);
        } else {
          timeoutId.current = window.setTimeout(processQueue, 0);
        }
      }
    },
    [processQueue, queue],
  );

  const trackEvent = useCallback(
    (eventName: string) => enqueue({ eventName: `${eventBase}:${eventName}` }),
    [eventBase, enqueue],
  );
  const trackNoBaseEvent = useCallback(
    (eventName: string) => enqueue({ eventName: eventName }),
    [enqueue],
  );
  const trackPage = useCallback(
    (pageName: string) => {
      setCurrentPage(pageName);
      enqueue({ pageName: `${pageBase}:${pageName}` });
    },
    [pageBase, enqueue],
  );

  return { trackEvent, trackPage, trackNoBaseEvent, AnalyticsScripts, currentPage };
}

interface QueueItem {
  eventName?: string;
  pageName?: string;
}

declare global {
  interface Window {
    s: {
      t: () => void;
      tl: (
        obj: any,
        type: "o" | "d" | "e",
        name?: string,
        variableOverrides?: { [vars: string]: string },
      ) => void;
      linkTrackVars: string;
      linkTrackEvents: string;
      channel?: string;
      pageName?: string;
      eVar53?: string;
      prop22?: string;
      events?: string;
    };
  }
}
